import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import {
  Paper,
  IconButton,
  Typography
} from '@material-ui/core'
import Icon from '../../../../components/atoms/Icon'
import { localStorageHelper } from '../../../../utils/localStorageHelper'

const PANEL_WIDTH_STORAGE_KEY = 'denali-ai-attachment-panel-width'
const MIN_PANEL_WIDTH = 350
const MAX_PANEL_WIDTH = 1200

const useStyles = makeStyles((theme) => ({
  panelWrapper: {
    height: '100%',
    overflow: 'hidden',
    flex: '0 0 auto',
    display: 'flex',
    animation: '$slideIn 0.3s ease-out',
    position: 'relative'
  },
  '@keyframes slideIn': {
    from: {
      opacity: 0,
      transform: 'translateX(100px)'
    },
    to: {
      opacity: 1,
      transform: 'translateX(0)'
    }
  },
  resizeHandle: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '6px',
    height: '100%',
    cursor: 'col-resize',
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      '& $resizeIndicator': {
        opacity: 1
      }
    }
  },
  resizeIndicator: {
    width: '3px',
    height: '40px',
    borderRadius: '2px',
    backgroundColor: 'var(--dai-color-border-accent)',
    opacity: 0,
    transition: 'opacity 150ms ease'
  },
  resizeHandleActive: {
    '& $resizeIndicator': {
      opacity: 1,
      backgroundColor: 'var(--dai-color-border-accent-hl)'
    }
  },
  panel: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    borderLeft: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    boxShadow: '-2px 0 4px rgba(0, 0, 0, 0.1)'
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'var(--padding-md)',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: 'var(--color-texture-300)',
    flexShrink: 0
  },
  panelContent: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  panelTitle: {
    fontWeight: 500,
    fontSize: '1.1rem',
    color: theme.palette.text.primary
  },
  closeButton: {
    marginLeft: theme.spacing(1)
  }
}))

function getInitialWidth (defaultWidth, skipStorage = false) {
  if (!skipStorage) {
    const stored = localStorageHelper.tryLoad(PANEL_WIDTH_STORAGE_KEY)
    if (stored && typeof stored === 'number' && stored >= MIN_PANEL_WIDTH && stored <= MAX_PANEL_WIDTH) {
      return stored
    }
  }
  // Parse default width string to number
  const parsed = parseInt(defaultWidth, 10)
  return isNaN(parsed) ? 600 : parsed
}

export function AttachmentPanel ({ open, onClose, title, children, width = '600px', headerActions }) {
  const classes = useStyles()
  const [panelWidth, setPanelWidth] = useState(() => getInitialWidth(width))
  const isDragging = useRef(false)
  const startX = useRef(0)
  const startWidth = useRef(0)

  const defaultWidth = useRef(getInitialWidth(width, true))

  const handleMouseDown = useCallback((e) => {
    e.preventDefault()
    isDragging.current = true
    startX.current = e.clientX
    startWidth.current = panelWidth
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [panelWidth])

  const handleDoubleClick = useCallback(() => {
    const resetWidth = defaultWidth.current
    setPanelWidth(resetWidth)
    localStorageHelper.tryStore(PANEL_WIDTH_STORAGE_KEY, resetWidth)
  }, [])

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging.current) return

      // Dragging left increases width, dragging right decreases
      const delta = startX.current - e.clientX
      const newWidth = Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, startWidth.current + delta))
      setPanelWidth(newWidth)
    }

    const handleMouseUp = () => {
      if (!isDragging.current) return

      isDragging.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''

      // Persist to localStorage
      setPanelWidth(current => {
        localStorageHelper.tryStore(PANEL_WIDTH_STORAGE_KEY, current)
        return current
      })
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  if (!open) return null

  return (
    <div
      className={classes.panelWrapper}
      style={{ width: `${panelWidth}px` }}
    >
      <div
        className={`${classes.resizeHandle} ${isDragging.current ? classes.resizeHandleActive : ''}`}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
      >
        <div className={classes.resizeIndicator} />
      </div>
      <Paper
        className={classes.panel}
        elevation={0}
      >
        <div className={classes.panelHeader}>
          <Typography className='font-serif font-size-lg' variant='h6'>
            {title || 'Attachment'}
          </Typography>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {headerActions}
            <IconButton
              onClick={onClose}
              className={classes.closeButton}
              size='small'
              aria-label='Close attachment viewer'
            >
              <Icon name='close' />
            </IconButton>
          </div>
        </div>

        <div className={classes.panelContent}>
          {children}
        </div>
      </Paper>
    </div>
  )
}

AttachmentPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node,
  width: PropTypes.string,
  headerActions: PropTypes.node
}
